(function attachJudesSetStartPrice(root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.JudesSetStartPrice = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesSetStartPrice() {
  'use strict';

  const MAX_MANIFEST_BYTES = 120 * 1024;
  const MAX_AGE_MS = 30 * 60 * 60 * 1000;
  const SCOPE = 'klassik-premium-initial-empty-cart-cache';
  const PRICING_IDENTITY_VERSION = 'set-price-identity-1';
  const PRODUCT_IDS = Object.freeze({ klassik: '6923750899901', premium: '6921605677245' });
  const EXPECTED_COUNTRY_PRICE_GROUPS = Object.freeze({
    'DE|EUR': 'eur', 'AT|EUR': 'eur', 'ES|EUR': 'eur', 'IT|EUR': 'eur', 'FR|EUR': 'eur', 'NL|EUR': 'eur',
    'US|USD': 'usd', 'GB|GBP': 'gbp', 'CH|CHF': 'chf', 'CA|CAD': 'cad', 'DK|DKK': 'dkk'
  });
  const ALL_RECIPE_IDS = Object.freeze([
    'klassik-all-in-s1', 'klassik-all-in-s2', 'klassik-comfort-s1', 'klassik-comfort-s2',
    'klassik-full-time-s1', 'klassik-full-time-s2', 'klassik-part-time-s1', 'klassik-part-time-s2',
    'premium-all-in-s12', 'premium-comfort-s12', 'premium-full-time-s12', 'premium-part-time-s12'
  ]);
  const EXPECTED_RECIPE_IDS = Object.freeze({
    [PRODUCT_IDS.klassik]: ALL_RECIPE_IDS,
    [PRODUCT_IDS.premium]: ALL_RECIPE_IDS
  });
  const identityFields = [
    'country', 'currency', 'priceGroupId', 'productId', 'recipeId', 'recipeVersion',
    'catalogVersion', 'pricingIdentityVersion', 'recipeFingerprint', 'sourceDigest', 'sourceVersion'
  ];

  function check(condition, message) {
    if (!condition) throw new Error(message);
  }

  function byteLength(value) {
    if (typeof Buffer !== 'undefined') return Buffer.byteLength(value, 'utf8');
    return new TextEncoder().encode(value).byteLength;
  }

  function stable(value) {
    if (Array.isArray(value)) return value.map(stable);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, stable(value[key])]));
  }

  function stableStringify(value) {
    return JSON.stringify(stable(value));
  }

  function fnv1a(value) {
    let hash = 0x811c9dc5;
    const bytes = typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(value) : Buffer.from(value, 'utf8');
    for (const byte of bytes) {
      hash ^= byte;
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
  }

  function aggregate(items) {
    check(Array.isArray(items), 'Invalid set items');
    const quantities = new Map();
    for (const item of items) {
      const id = String(item.id || item.variantId || '');
      const quantity = Number(item.quantity);
      check(/^\d+$/.test(id) && Number.isSafeInteger(quantity) && quantity > 0, 'Invalid set item');
      quantities.set(id, (quantities.get(id) || 0) + quantity);
    }
    return [...quantities.entries()].sort(([left], [right]) => left.localeCompare(right))
      .map(([id, quantity]) => ({ id, quantity }));
  }

  function descriptor(recipe, productId) {
    check(recipe && typeof recipe === 'object' && typeof recipe.id === 'string' && recipe.id, 'Invalid set recipe');
    const bundleType = String(recipe.bundleType || '');
    const owner = String(productId || recipe.productId || PRODUCT_IDS[bundleType] || '');
    check(EXPECTED_RECIPE_IDS[owner]?.includes(recipe.id), 'Set recipe owner differs');
    const recipeOwnerProductId = String(recipe.productId || PRODUCT_IDS[bundleType]
      || (recipe.id.startsWith('klassik-') ? PRODUCT_IDS.klassik : recipe.id.startsWith('premium-') ? PRODUCT_IDS.premium : ''));
    check((recipe.id.startsWith('klassik-') && recipeOwnerProductId === PRODUCT_IDS.klassik)
      || (recipe.id.startsWith('premium-') && recipeOwnerProductId === PRODUCT_IDS.premium), 'Set recipe bundle owner differs');
    const discountCode = String(recipe.discountCode || recipe.discountCodes?.standardDiscount || '').toLowerCase();
    check(discountCode, 'Missing set recipe discount');
    const fixedItems = aggregate((recipe.fixedItems || []).map((item) => ({
      id: item.id || item.variantId,
      quantity: item.quantity
    })));
    const rawGroups = recipe.equivalenceGroups || recipe.choiceGroups || [];
    const equivalenceGroups = rawGroups.map((group) => {
      const id = String(group.id || '');
      const quantity = Number(group.quantity);
      const allowedVariantIds = [...(group.allowedVariantIds || [])].map(String).sort();
      check(id && Number.isSafeInteger(quantity) && quantity > 0 && allowedVariantIds.length > 0
        && new Set(allowedVariantIds).size === allowedVariantIds.length
        && allowedVariantIds.every((variantId) => /^\d+$/.test(variantId)), 'Invalid set equivalence group');
      return { id, quantity, allowedVariantIds };
    }).sort((left, right) => left.id.localeCompare(right.id));
    check(equivalenceGroups.length > 0 && new Set(equivalenceGroups.map((group) => group.id)).size === equivalenceGroups.length,
      'Invalid set equivalence groups');
    return { productId: owner, recipeOwnerProductId, recipeId: recipe.id, discountCode, fixedItems, equivalenceGroups };
  }

  function recipeFingerprint(recipe, productId) {
    return `set-recipe-fnv1a32-${fnv1a(stableStringify(descriptor(recipe, productId)))}`;
  }

  function catalogRow(catalog, id) {
    const value = catalog && catalog[String(id)];
    check(value && typeof value === 'object', `Set catalog variant ${id} is missing`);
    const priceMinor = Number(value.priceMinor);
    check(Number.isSafeInteger(priceMinor) && priceMinor >= 0 && typeof value.available === 'boolean',
      `Set catalog variant ${id} is invalid`);
    return { priceMinor, available: value.available };
  }

  function catalogVersion(recipe, catalog, productId) {
    const value = descriptor(recipe, productId);
    const fixed = value.fixedItems.map((item) => {
      const row = catalogRow(catalog, item.id);
      check(row.available, `Fixed set variant ${item.id} is unavailable`);
      return [item.id, item.quantity, row.priceMinor];
    });
    const groups = value.equivalenceGroups.map((group) => {
      const rows = group.allowedVariantIds.map((id) => {
        const row = catalogRow(catalog, id);
        return [id, row.priceMinor];
      });
      const prices = new Set(rows.map((row) => row[1]));
      check(prices.size === 1, `Set design prices are not equivalent for ${group.id}`);
      return [group.id, group.quantity, rows];
    });
    return `set-catalog-fnv1a32-${fnv1a(stableStringify({ fixed, groups }))}`;
  }

  function retailValueMinor(recipe, catalog, productId) {
    const value = descriptor(recipe, productId);
    const fixed = value.fixedItems.reduce((sum, item) => {
      const row = catalogRow(catalog, item.id);
      check(row.available, `Fixed set variant ${item.id} is unavailable`);
      return sum + row.priceMinor * item.quantity;
    }, 0);
    const choices = value.equivalenceGroups.reduce((sum, group) => {
      const rows = group.allowedVariantIds.map((id) => catalogRow(catalog, id));
      const prices = new Set(rows.map((row) => row.priceMinor));
      check(prices.size === 1, `Set design prices are not equivalent for ${group.id}`);
      return sum + rows[0].priceMinor * group.quantity;
    }, 0);
    return fixed + choices;
  }

  function validateSelection(recipe, selections, catalog, productId) {
    const value = descriptor(recipe, productId);
    for (const group of value.equivalenceGroups) {
      const selected = selections?.[group.id];
      check(Array.isArray(selected) && selected.length === group.quantity, `Invalid set selection for ${group.id}`);
      for (const id of selected.map(String)) {
        check(group.allowedVariantIds.includes(id) && catalogRow(catalog, id).available,
          `Unproved set selection for ${group.id}`);
      }
    }
    return value;
  }

  function manifestKey(value) {
    const raw = JSON.stringify(identityFields.map((field) => String(value[field] || '')));
    return `set-v2:${fnv1a(raw)}${fnv1a([...raw].reverse().join(''))}`;
  }

  function validateManifest(manifest) {
    const expectedRecipes = EXPECTED_RECIPE_IDS[String(manifest?.productId || '')];
    const expectedCount = expectedRecipes ? expectedRecipes.length * Object.keys(EXPECTED_COUNTRY_PRICE_GROUPS).length : 0;
    if (!manifest || manifest.schemaVersion !== 2 || manifest.scope !== SCOPE || manifest.complete !== true
      || !expectedRecipes || typeof manifest.generationId !== 'string' || !manifest.generationId
      || !/^[a-f0-9]{64}$/.test(manifest.sourceDigest)
      || manifest.sourceVersion !== `admin-source-sha256:${manifest.sourceDigest}`
      || !/^[a-f0-9]{64}$/.test(manifest.localSourceDigest)
      || !Number.isSafeInteger(manifest.generatedAt) || !Number.isSafeInteger(manifest.expiresAt)
      || !Number.isSafeInteger(manifest.maxAgeMs) || manifest.maxAgeMs <= 0 || manifest.maxAgeMs > MAX_AGE_MS
      || manifest.expiresAt <= manifest.generatedAt || manifest.expiresAt - manifest.generatedAt > manifest.maxAgeMs
      || stableStringify(manifest.countryPriceGroups) !== stableStringify(EXPECTED_COUNTRY_PRICE_GROUPS)
      || stableStringify([...(manifest.requiredRecipeIds || [])].sort()) !== stableStringify(expectedRecipes)
      || !manifest.records || typeof manifest.records !== 'object' || Array.isArray(manifest.records)
      || manifest.recordCount !== expectedCount || Object.keys(manifest.records).length !== expectedCount
      || !manifest.renderIndex || typeof manifest.renderIndex !== 'object' || Array.isArray(manifest.renderIndex)
      || Object.keys(manifest.renderIndex).length !== 11) return null;
    const seen = new Set();
    for (const [marketKey, priceGroupId] of Object.entries(EXPECTED_COUNTRY_PRICE_GROUPS)) {
      const [country, currency] = marketKey.split('|');
      const index = manifest.renderIndex[marketKey];
      if (!index || stableStringify(Object.keys(index).sort()) !== stableStringify(expectedRecipes)) return null;
      for (const recipeId of expectedRecipes) {
        const key = index[recipeId];
        const record = manifest.records[key];
        const combination = `${marketKey}|${recipeId}`;
        if (seen.has(combination) || typeof key !== 'string' || !record
          || record.schemaVersion !== 2 || record.scope !== 'initial-empty-cart'
          || record.generationId !== manifest.generationId || record.sourceDigest !== manifest.sourceDigest
          || record.sourceVersion !== manifest.sourceVersion || record.generatedAt !== manifest.generatedAt
          || record.expiresAt !== manifest.expiresAt || record.country !== country || record.currency !== currency
          || record.priceGroupId !== priceGroupId || record.productId !== manifest.productId || record.recipeId !== recipeId
          || record.pricingIdentityVersion !== PRICING_IDENTITY_VERSION
          || !identityFields.every((field) => typeof record[field] === 'string' && record[field])
          || manifestKey(record) !== key
          || !['retailValueMinor', 'finalPriceMinor', 'discountAmountMinor']
            .every((field) => Number.isSafeInteger(record[field]) && record[field] >= 0)
          || record.retailValueMinor - record.discountAmountMinor !== record.finalPriceMinor) return null;
        seen.add(combination);
      }
    }
    return seen.size === expectedCount ? manifest : null;
  }

  function parse(raw) {
    if (typeof raw !== 'string' || byteLength(raw) > MAX_MANIFEST_BYTES) return null;
    try { return validateManifest(JSON.parse(raw)); } catch (error) { return null; }
  }

  function resolve(manifest, selection, now = Date.now()) {
    try {
      if (!validateManifest(manifest) || manifest.generatedAt > now || manifest.expiresAt <= now
        || now - manifest.generatedAt > manifest.maxAgeMs) return null;
      const productId = String(selection.productId || '');
      if (productId !== manifest.productId) return null;
      const marketKey = `${String(selection.country || '').toUpperCase()}|${String(selection.currency || '').toUpperCase()}`;
      const priceGroupId = manifest.countryPriceGroups[marketKey];
      if (!priceGroupId || (selection.priceGroupId && selection.priceGroupId !== priceGroupId)) return null;
      const recipe = selection.recipe;
      validateSelection(recipe, selection.selections, selection.catalog, productId);
      const wanted = {
        country: marketKey.slice(0, 2),
        currency: marketKey.slice(3),
        priceGroupId,
        productId,
        recipeId: recipe.id,
        recipeVersion: String(selection.recipeVersion || ''),
        catalogVersion: catalogVersion(recipe, selection.catalog, productId),
        pricingIdentityVersion: String(selection.pricingIdentityVersion || ''),
        recipeFingerprint: recipeFingerprint(recipe, productId),
        sourceDigest: manifest.sourceDigest,
        sourceVersion: manifest.sourceVersion
      };
      if (!manifest.requiredRecipeIds.includes(wanted.recipeId)) return null;
      const record = manifest.records[manifestKey(wanted)];
      if (!record || !identityFields.every((field) => record[field] === wanted[field])) return null;
      return Object.freeze({
        currency: record.currency,
        retailValueMinor: record.retailValueMinor,
        finalPriceMinor: record.finalPriceMinor,
        discountAmountMinor: record.discountAmountMinor,
        displayValid: true,
        checkoutVerified: false,
        generationId: record.generationId,
        expiresAt: record.expiresAt,
        productId: record.productId,
        recipeId: record.recipeId,
        recipeFingerprint: record.recipeFingerprint,
        catalogVersion: record.catalogVersion,
        sourceDigest: record.sourceDigest,
        sourceVersion: record.sourceVersion,
        country: record.country,
        currency: record.currency,
        recordKey: manifestKey(record)
      });
    } catch (error) {
      return null;
    }
  }

  function serverNodeMatches(seed, dataset) {
    return !!seed && !!dataset && dataset.generationId === seed.generationId
      && dataset.sourceDigest === seed.sourceDigest && dataset.sourceVersion === seed.sourceVersion
      && dataset.productId === seed.productId && dataset.recipeId === seed.recipeId
      && dataset.recipeFingerprint === seed.recipeFingerprint && dataset.catalogVersion === seed.catalogVersion
      && dataset.country === seed.country && dataset.currency === seed.currency
      && Number(dataset.expiresAt) === seed.expiresAt
      && Number(dataset.finalPriceMinor) === seed.finalPriceMinor;
  }

  function routeAllowsCache(href, startklarEnabled) {
    try {
      const url = new URL(String(href || ''), 'https://www.judesfamily.com');
      return !startklarEnabled && !url.searchParams.has('variant');
    } catch (error) {
      return false;
    }
  }

  function resolveForRoute(manifest, selection, cacheRouteEligible, now = Date.now()) {
    return cacheRouteEligible === true ? resolve(manifest, selection, now) : null;
  }

  async function refreshDisplay(options) {
    options.setReady(false);
    if (options.seed) options.paintSeed(options.seed);
    try {
      const exact = await options.quote();
      if (!options.isCurrent()) return null;
      options.paintExact(exact);
      options.setReady(true);
      return exact;
    } catch (error) {
      if (options.isCurrent() && !options.seed && options.paintFallback) options.paintFallback(error);
      throw error;
    }
  }

  return Object.freeze({
    MAX_MANIFEST_BYTES, MAX_AGE_MS, SCOPE, PRICING_IDENTITY_VERSION, PRODUCT_IDS,
    EXPECTED_COUNTRY_PRICE_GROUPS, EXPECTED_RECIPE_IDS, ALL_RECIPE_IDS, stableStringify, descriptor,
    recipeFingerprint, catalogVersion, retailValueMinor, validateSelection, manifestKey,
    validateManifest, parse, resolve, resolveForRoute, serverNodeMatches, routeAllowsCache, refreshDisplay
  });
}));
